
import React, { useState, useEffect } from "react";
import { Sparkles, RefreshCw, Copy, Share2 } from "lucide-react";
import { ElfNameGeneratorTheme } from "./ElfNameGeneratorTheme";
import { GeneratorForm } from "./GeneratorForm";
import { GeneratedName } from "./GeneratedName";
import { NameHistory } from "./NameHistory";
import { generateElfName } from "./useElfNameGenerator";
import { loadNameComponents } from "./nameCache";
import { useToolUsageTracker } from "@/utils/toolUsageTracker";

export interface ElfName {
  id: string;
  firstName: string;
  lastName?: string;
  timestamp: number;
}

interface ElfNameGeneratorProps {
  title?: string;
  showHistory?: boolean;
  instanceId?: string;
  postId?: string;
}

export const ElfNameGenerator: React.FC<ElfNameGeneratorProps> = ({
  title = "Christmas Elf Name Generator",
  showHistory = true,
  instanceId,
  postId
}) => {
  const [fullName, setFullName] = useState(true);
  const [currentName, setCurrentName] = useState<ElfName | null>(null);
  const [nameHistory, setNameHistory] = useState<ElfName[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Use the tracker hook
  const { trackToolUsage } = useToolUsageTracker("elfNameGenerator", postId);

  // Load history from localStorage
  useEffect(() => {
    const savedHistory = localStorage.getItem("elfNameHistory");
    if (savedHistory) {
      try {
        setNameHistory(JSON.parse(savedHistory));
      } catch (e) {
        console.error("Failed to parse elf name history", e);
      }
    }

    void loadNameComponents();
  }, []);

  // Save history to localStorage when it changes
  useEffect(() => {
    if (nameHistory.length > 0) {
      localStorage.setItem("elfNameHistory", JSON.stringify(nameHistory));
    }
  }, [nameHistory]);

  const handleGenerate = () => {
    setIsGenerating(true);
    
    // Track usage
    trackToolUsage({
      action: "generate",
      metadata: {
        fullName,
        instanceId
      }
    });
    
    setTimeout(() => {
      const newName = generateElfName(fullName);
      setCurrentName(newName);
      
      // Add to history
      setNameHistory(prev => [newName, ...prev].slice(0, 10));
      setIsGenerating(false);
    }, 800); // Add a slight delay for animation
  };

  const copyToClipboard = () => {
    if (!currentName) return;
    
    const textToCopy = fullName && currentName.lastName 
      ? `${currentName.firstName} ${currentName.lastName}`
      : currentName.firstName;
      
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopied(true);
      trackToolUsage({
        action: "copy",
        metadata: {
          instanceId
        }
      });
      
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleShare = () => {
    if (!currentName) return;
    
    const text = fullName && currentName.lastName
      ? `My Christmas elf name is ${currentName.firstName} ${currentName.lastName}! Find yours at`
      : `My Christmas elf name is ${currentName.firstName}! Find yours at`;
      
    if (navigator.share) {
      navigator.share({
        title: 'My Christmas Elf Name',
        text: text,
        url: window.location.href,
      }).then(() => {
        trackToolUsage({
          action: "share",
          metadata: {
            instanceId
          }
        });
      });
    } else {
      copyToClipboard();
    }
  };

  const selectFromHistory = (name: ElfName) => {
    setCurrentName(name);
    
    trackToolUsage({
      action: "selectHistory",
      metadata: {
        instanceId
      }
    });
  };

  const clearHistory = () => {
    setNameHistory([]);
    localStorage.removeItem("elfNameHistory");
    
    trackToolUsage({
      action: "clearHistory",
      metadata: {
        instanceId
      }
    });
  };

  return (
    <div className={ElfNameGeneratorTheme.container}>
      <div className={ElfNameGeneratorTheme.header}>
        <Sparkles className={ElfNameGeneratorTheme.headerIcon} />
        <h2 className={ElfNameGeneratorTheme.title}>{title}</h2>
      </div>
      
      <div className={ElfNameGeneratorTheme.content}>
        <GeneratorForm 
          fullName={fullName}
          setFullName={setFullName}
          onGenerate={handleGenerate}
          isGenerating={isGenerating}
        />
        
        {currentName && (
          <GeneratedName 
            name={currentName}
            fullName={fullName}
            isGenerating={isGenerating}
            onCopy={copyToClipboard}
            onShare={handleShare}
            copied={copied}
          />
        )}
        
        {showHistory && nameHistory.length > 0 && (
          <NameHistory 
            history={nameHistory}
            onSelect={selectFromHistory}
            onClear={clearHistory}
          />
        )}
      </div>
    </div>
  );
};
